import React, { useMemo, useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import CourseCard from '../../../components/CourseCard';
import { BookOpen } from 'lucide-react';
import PageLoader from '../../../components/PageLoader';

import { getCourses as apiGetCourses, getMyCoursePurchases } from '../../../api';

const UserCourses = () => {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [apiCourses, setApiCourses] = useState(null);
  const [dbPurchasedSet, setDbPurchasedSet] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [purchaseBanner, setPurchaseBanner] = useState(null);

  const purchasedKey = user?.id ? String(user.id) : '';

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const data = await apiGetCourses();
        if (!cancelled) setApiCourses(Array.isArray(data) ? data : []);
      } catch {
        if (!cancelled) setApiCourses([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [purchasedKey]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!user?.id) {
        if (!cancelled) setDbPurchasedSet(new Set());
        return;
      }
      try {
        const data = await getMyCoursePurchases();
        if (!cancelled) setDbPurchasedSet(new Set((data?.purchasedCourseIds || []).map(String)));
      } catch {
        if (!cancelled) setDbPurchasedSet(new Set());
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.id, purchasedKey]);

  useEffect(() => {
    if (location.state?.coursePurchaseSuccess) {
      const title = location.state.courseTitle;
      setPurchaseBanner(
        title
          ? `"${title}" is now in your purchased courses.`
          : 'Course added to your purchased courses.'
      );
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, location.pathname, navigate]);

  const allCourses = useMemo(() => (Array.isArray(apiCourses) ? apiCourses : []), [apiCourses]);

  /** Show courses from individual purchases OR active subscription plans. */
  const myCourses = allCourses.filter((c) => {
    const isPurchased = dbPurchasedSet.has(String(c.id));
    const isSubscribed = (user?.subscriptionFreeCourseIds || []).some((id) => String(id) === String(c.id));
    return isPurchased || isSubscribed;
  });

  return (
    <div className=" mx-auto">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">My Courses</h1>
        <p className="text-gray-500 mt-1">
          Courses you have purchased. Click a course to view lessons.
        </p>
      </div>

      {purchaseBanner && (
        <div className="mb-6 p-4 rounded-xl bg-primary-50 border border-primary-100 text-primary-900 text-sm flex flex-wrap items-center justify-between gap-2">
          <span>{purchaseBanner}</span>
          <button
            type="button"
            onClick={() => setPurchaseBanner(null)}
            className="text-primary-700 font-medium hover:underline shrink-0"
          >
            Dismiss
          </button>
        </div>
      )}

      {loading ? (
        <PageLoader fullScreen={false} />
      ) : myCourses.length === 0 ? (

        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-12 text-center">
          <div className="w-16 h-16 rounded-2xl bg-gray-100 flex items-center justify-center mx-auto mb-4">
            <BookOpen className="w-8 h-8 text-gray-400" />
          </div>
          <h2 className="text-lg font-semibold text-gray-800">No courses yet</h2>
          <p className="text-gray-500 mt-2 max-w-sm mx-auto">
            You haven’t purchased any course yet. Browse the catalogue to buy a course.
          </p>
          <Link
            to="/user/browse"
            className="inline-flex items-center gap-2 mt-6 px-4 py-2 rounded-xl bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 transition-colors"
          >
            Browse courses
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 4xl:grid-cols-5 gap-6">
          {myCourses.map((course) => (
            <CourseCard
              key={course.id}
              course={course}
              isEnrolled
              viewCoursePath={`/user/courses/${course.id}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default UserCourses;
